/**
 * Account Service
 * 
 * Provides API calls for account management functionality.
 * Handles both Entra-synced fields and database-only preferences.
 * 
 * Data Sources:
 * - Entra-synced: GivenName, Surname, Name (DisplayName), Country, Email
 * - Database-only: ReplyToEmail, ProfilePictureUrl, Card Reminder settings, Schedule Reminder settings,
 *   Unopened e-card follow-up settings (PBI #1160)
 */

import apiClient from './apiClient';

/**
 * Full account information including Entra-synced and database-only fields.
 */
export interface AccountInfo {
  id: string;
  
  // Entra-synced fields
  email: string;
  name?: string | null;
  givenName?: string | null;
  surname?: string | null;
  country?: string | null;
  
  // Database-only fields
  replyToEmail?: string | null;
  /**
   * Read-only. When false, My Account hides Reply-To and saves do not send replyToEmail.
   * Mirrors AppSettings:IncludeReplyToOnDeliveryEmails on the API.
   */
  includeReplyToOnDeliveryEmails?: boolean;
  profilePictureUrl?: string | null;
  /**
   * User's phone number. Used as default sender phone for flower orders.
   * Optional in My Account; required at flower-order placement time and
   * when enabling Flowers AutoSend.
   */
  phone?: string | null;

  // Home address fields (database-only)
  // Physical address used as the sender (CUSTOMER) address for Florist One
  // flower orders. All optional in My Account; required at order placement
  // time and when enabling Flowers AutoSend.
  /** Street address line 1 of the user's home address. */
  addressLine1?: string | null;
  /** Street address line 2 — apartment, suite, etc. */
  addressLine2?: string | null;
  /** City of the user's home address. */
  addressCity?: string | null;
  /** State or province of the user's home address. */
  addressState?: string | null;
  /** Postal / ZIP code of the user's home address. */
  addressPostalCode?: string | null;
  /**
   * ISO 3166-1 alpha-2 country code (e.g. "US", "CA").
   * Distinct from the Entra-synced `country` profile field above.
   */
  addressCountry?: string | null;

  // Card reminder settings (database-only)
  // Reminders about scheduled cards that are about to be sent
  reminder1Enabled: boolean;
  reminder1DaysBefore: number;
  reminder2Enabled: boolean;
  reminder2DaysBefore: number;
  
  // Schedule reminder settings (database-only)
  // Prompts to schedule a card for an upcoming event
  scheduleReminder1Enabled: boolean;
  scheduleReminder1DaysBefore: number;
  scheduleReminder2Enabled: boolean;
  scheduleReminder2DaysBefore: number;

  /**
   * When false, the user opts out of marketing nurture drip emails (PBI #1090).
   * Transactional mail (welcome, occasion reminders) may still send per product rules.
   */
  marketingEmailsEnabled: boolean;

  /**
   * Whether the first unopened e-card follow-up email is enabled (PBI #1160).
   * Default: true.
   */
  unopenedFollowUp1Enabled: boolean;
  /**
   * Hours after send before the first unopened follow-up. Default: 24. Range: 1-168.
   */
  unopenedFollowUp1AfterHours: number;
  /**
   * Whether the second unopened e-card follow-up email is enabled. Default: true.
   */
  unopenedFollowUp2Enabled: boolean;
  /**
   * Hours after send before the second unopened follow-up. Default: 120. Range: 2-720;
   * must be greater than follow-up 1 when both are enabled.
   */
  unopenedFollowUp2AfterHours: number;

  /** Default for new E-Card enrollments; null/omitted means Remind. */
  defaultScheduleMode?: 'Notify' | 'AutoSend' | null;

  /** How contact import treats rows that exactly match an existing contact. */
  contactImportExistingMode?: 'Skip' | 'FillBlanks';
}

/**
 * Request to update database-only preferences.
 */
export interface UpdatePreferencesRequest {
  replyToEmail?: string | null;
  /**
   * User's phone number. Optional in My Account preferences (the backend
   * accepts saves with no phone). Required at flower-order placement time
   * and when enabling Flowers AutoSend — those checks live closer to the
   * relevant operation.
   */
  phone?: string | null;

  // Home address fields — same null/empty convention as phone.
  // null = keep existing value; empty string = clear the field.
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
  /** ISO 3166-1 alpha-2 country code (populated via CountrySelect). */
  addressCountry?: string | null;

  // Card reminder settings
  reminder1Enabled?: boolean | null;
  reminder1DaysBefore?: number | null;
  reminder2Enabled?: boolean | null;
  reminder2DaysBefore?: number | null;

  // Schedule reminder settings
  scheduleReminder1Enabled?: boolean | null;
  scheduleReminder1DaysBefore?: number | null;
  scheduleReminder2Enabled?: boolean | null;
  scheduleReminder2DaysBefore?: number | null;

  /** Opt-in/out for marketing nurture lifecycle emails. */
  marketingEmailsEnabled?: boolean | null;

  /** First unopened e-card follow-up toggle (PBI #1160). */
  unopenedFollowUp1Enabled?: boolean | null;
  /** Hours after send for first unopened follow-up. */
  unopenedFollowUp1AfterHours?: number | null;
  /** Second unopened e-card follow-up toggle. */
  unopenedFollowUp2Enabled?: boolean | null;
  /** Hours after send for second unopened follow-up. */
  unopenedFollowUp2AfterHours?: number | null;

  /** E-Card default for new contact enrollments (PBI #1362). */
  defaultScheduleMode?: 'Notify' | 'AutoSend' | null;

  contactImportExistingMode?: 'Skip' | 'FillBlanks' | null;
}

/**
 * Request to update Entra-synced profile fields.
 */
export interface UpdateProfileRequest {
  name?: string | null;
  givenName?: string | null;
  surname?: string | null;
  country?: string | null;
}

/**
 * Response from profile update including Entra sync status.
 */
export interface ProfileUpdateResponse {
  success: boolean;
  entraSyncSuccess: boolean;
  entraSyncMessage?: string | null;
  account?: AccountInfo | null;
}

/**
 * A tracking-eligible sent e-card that has not been confirmed open (Overview / Contacts).
 */
export interface UnopenedEcardWarning {
  scheduledCardId: number;
  contactId: number;
  recipientFirstName: string;
  eventTypeName: string;
  sentDateUtc: string;
}

/**
 * Account Service
 * 
 * Provides methods for managing user account data.
 */
export const accountService = {
  /**
   * Get the current user's full account information.
   * Combines Entra claims with database preferences.
   */
  async getAccount(): Promise<AccountInfo> {
    const response = await apiClient.get<AccountInfo>('/account');
    return response.data;
  },

  /**
   * Update database-only preferences.
   * These fields are NOT synced to Entra.
   * 
   * @param preferences - The preferences to update
   */
  async updatePreferences(preferences: UpdatePreferencesRequest): Promise<AccountInfo> {
    const response = await apiClient.put<AccountInfo>('/account/preferences', preferences);
    return response.data;
  },

  /**
   * Update Entra-synced profile fields.
   * Changes are synced to Entra and also cached in the local database.
   * 
   * @param profile - The profile fields to update
   */
  async updateProfile(profile: UpdateProfileRequest): Promise<ProfileUpdateResponse> {
    const response = await apiClient.put<ProfileUpdateResponse>('/account/profile', profile);
    return response.data;
  },

  /**
   * Update both preferences and profile in a single operation.
   * This is a convenience method that calls both endpoints.
   * 
   * @param data - Combined preferences and profile data
   */
  async updateAccount(data: UpdatePreferencesRequest & UpdateProfileRequest): Promise<{
    account: AccountInfo;
    entraSyncSuccess: boolean;
    entraSyncMessage?: string | null;
  }> {
    // Separate the data into preferences and profile.
    // Omit replyToEmail when not provided so a hidden field (global Reply-To off) cannot clear DB state.
    const preferences: UpdatePreferencesRequest = {
      ...(data.replyToEmail !== undefined && { replyToEmail: data.replyToEmail }),
      phone: data.phone,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      addressCity: data.addressCity,
      addressState: data.addressState,
      addressPostalCode: data.addressPostalCode,
      addressCountry: data.addressCountry,
      reminder1Enabled: data.reminder1Enabled,
      reminder1DaysBefore: data.reminder1DaysBefore,
      reminder2Enabled: data.reminder2Enabled,
      reminder2DaysBefore: data.reminder2DaysBefore,
      scheduleReminder1Enabled: data.scheduleReminder1Enabled,
      scheduleReminder1DaysBefore: data.scheduleReminder1DaysBefore,
      scheduleReminder2Enabled: data.scheduleReminder2Enabled,
      scheduleReminder2DaysBefore: data.scheduleReminder2DaysBefore,
      marketingEmailsEnabled: data.marketingEmailsEnabled,
      unopenedFollowUp1Enabled: data.unopenedFollowUp1Enabled,
      unopenedFollowUp1AfterHours: data.unopenedFollowUp1AfterHours,
      unopenedFollowUp2Enabled: data.unopenedFollowUp2Enabled,
      unopenedFollowUp2AfterHours: data.unopenedFollowUp2AfterHours,
    };
    
    const profile: UpdateProfileRequest = {
      name: data.name,
      givenName: data.givenName,
      surname: data.surname,
      country: data.country,
    };

    // Update profile first (syncs to Entra)
    const profileResult = await this.updateProfile(profile);
    
    // Then update preferences (database only, includes reminder settings)
    const account = await this.updatePreferences(preferences);

    return {
      account,
      entraSyncSuccess: profileResult.entraSyncSuccess,
      entraSyncMessage: profileResult.entraSyncMessage,
    };
  },

  /**
   * Lists unopened e-card warnings for Overview banners and Contacts pills (PBI #1160).
   */
  async getUnopenedEcardWarnings(): Promise<UnopenedEcardWarning[]> {
    const response = await apiClient.get<UnopenedEcardWarning[]>('/account/unopened-ecard-warnings');
    return response.data;
  },

  /**
   * Dismisses the unopened warning for a single scheduled card.
   *
   * @param scheduledCardId - Card whose Overview / Contacts urgency should clear
   */
  async dismissUnopenedWarningForCard(scheduledCardId: number): Promise<void> {
    await apiClient.post(`/scheduledcards/${scheduledCardId}/dismiss-unopened-warning`);
  },

  /**
   * Dismisses all qualifying unopened warnings for a contact.
   *
   * @param contactId - Contact whose unopened e-card pills / banners should clear
   */
  async dismissUnopenedWarningsForContact(contactId: number): Promise<void> {
    await apiClient.post(`/contacts/${contactId}/dismiss-unopened-warnings`);
  },
};

export default accountService;
